
import {
  Component, Ref, Watch, Mixins,
} from 'vue-property-decorator';
import { requiredRules } from '@/features/common/helpers/validationRules';
import { GlobalResponseType, VForm } from '@/features/common/types';
import { showErrorAlertDialog, showSuccessAlertDialog } from '@/features/common/helpers';
import NoResultsFound from '@/features/common/components/NoResultsFound.vue';
import PasswordInput from '@/features/common/components/PasswordInput.vue';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import legacyMixin from '@/features/common/mixins/legacyMixin';
import { CarrierCodeType } from '@/features/shipment/types';
import MerchantAccountMixin from '../mixins/mixin';
import PaymentMethodSelect from '../components/PaymentMethodSelect.vue';
import {
  addCarrierAccount,
  CarrierAccountResponseType,
  editCarrierAccount,
  getAllAvailableServiceAccounts,
  getCarrierAccountById,
} from '../services/carrierAccounts.services';
import { CarrierAccountType, ExtensionInputType } from '../types';
import { isRegisteredThroughMerchant, isFedexRegistration } from '../helpers';
import { registerUPSMerchant } from '../services/merchantRegistration.services';

interface formDataType {
  [key: string]: {
    value: string | boolean,
    isVisible: boolean,
    showUrl: string,
    hint: string,
    displayLabel: string,
    isRequired: boolean,
    type: ExtensionInputType,
  }
}

interface ElementDataType {
  tag: string,
  type: string,
  distinctKey: string,
  autocomplete: string,
}

@Component({
  components: {
    PaymentMethodSelect,
    NoResultsFound,
    PasswordInput,
  },
})
export default class AddCarrierAccount extends Mixins(legacyMixin, MerchantAccountMixin) {
  @Ref('form') form!: VForm;

  private isLoading = false;

  private requiredRules = requiredRules;

  private formData: formDataType = {};

  private carrierAccount: CarrierAccountType | null = null;

  private currentSubmissionOrder = 0;

  get carrierCodeQuery(): string {
    return this.$route.query.carrierCode as string;
  }

  get accountIdQuery(): string {
    return this.$route.query.accountId as string;
  }

  get isEditPage(): boolean {
    return this.$route.name === 'carrier-accounts-edit';
  }

  get isIncompleteInstallPage(): boolean {
    return !this.isEditPage && !!this.accountIdQuery;
  }

  get pageTitle(): string {
    if (this.isEditPage) {
      return this.$t('carrierAccounts.editAccount.pageTitle', {
        carrier: this.carrierCodeQuery,
      }) as string;
    }

    return this.$t('carrierAccounts.addAccount.pageTitle', {
      carrier: this.carrierCodeQuery,
    }) as string;
  }

  get isFormDataAvailable(): boolean {
    return !!Object.keys(this.formData).length;
  }

  get isPageLoading(): boolean {
    return !!this.$store.state.loading;
  }

  get isShowInputsForm(): boolean {
    if (!this.carrierCodeQuery) return false;
    if (this.isLegacyFeatures) return true;
    return !isRegisteredThroughMerchant(
      this.carrierCodeQuery as (keyof typeof CarrierCodeType),
    );
  }

  @Watch('accountIdQuery', { immediate: true })
  private async loadExistingFormDataIfAny(accountId: string) {
    if (!accountId) return;
    if (!this.accountIdQuery) return;

    this.$store.commit('setLoading', true);
    const response = await getCarrierAccountById(+this.accountIdQuery);
    this.$store.commit('setLoading', false);

    if (response && response.successful && response.carrierAccount) {
      this.carrierAccount = response.carrierAccount;
      this.populateForm();
      return;
    }

    this.redirectToCarrierAccountPage();
  }

  @Watch('carrierCodeQuery', { immediate: true })
  private async updateAccountsOnCarrierCodeChange() {
    await this.fetchAndSetAvailableAccounts();
  }

  async mounted(): Promise<void> {
    await this.fetchAndSetAvailableAccounts();
  }

  private async fetchAndSetAvailableAccounts(): Promise<void> {
    if (this.isEditPage || this.isIncompleteInstallPage) return;

    this.$store.commit('setLoading', true);
    // TODO: fetch only the account by carrier code instead of all (need backend endpoint)
    await this.fetchAvailableAccounts();
    this.$store.commit('setLoading', false);
  }

  private async fetchAvailableAccounts() {
    const accounts = await getAllAvailableServiceAccounts();

    if (accounts && accounts.successful && accounts.data && accounts.data.length) {
      const carrierAccount = accounts.data.find(
        (account) => account.carrierCode === this.carrierCodeQuery,
      ) ?? null;

      if (!carrierAccount) return;

      for (let i = 0; i < carrierAccount.extensions.length; i += 1) {
        if (!carrierAccount.extensions[i].value
          && carrierAccount.extensions[i].type !== ExtensionInputType.boolean) {
          carrierAccount.extensions[i].value = '';
        }
      }

      this.carrierAccount = carrierAccount as CarrierAccountType;

      this.populateForm();
    }
  }

  private populateForm(currentOrder = 0) {
    if (!this.carrierAccount) return;

    const { extensions } = this.carrierAccount;
    if (!extensions) return;

    this.formData = {};

    for (let i = 0; i < extensions.length; i += 1) {
      const currentExtension = extensions[i];

      let formValue: string | boolean | null = null;
      if (this.formData[currentExtension.name]) {
        formValue = this.formData[currentExtension.name].value;
      }

      const formOption = {
        displayLabel: currentExtension.displayName,
        isRequired: currentExtension.isRequired,
        value: currentExtension.value || formValue,
        isVisible: false,
        showUrl: '',
        hint: '',
        type: currentExtension.type,
      };

      if (currentExtension.type === ExtensionInputType.boolean) {
        formOption.isVisible = true;
        const val = currentExtension.value;
        if (val === undefined || val === null || val === '') {
          formOption.value = true;
        } else if (typeof val === 'string') {
          formOption.value = val.toLowerCase() !== 'false';
        } else {
          formOption.value = !!val;
        }
      } else if (currentOrder === currentExtension.order) {
        formOption.isVisible = true;
      }
      this.$set(this.formData, currentExtension.name, formOption);
    }
  }

  private async submit() {
    if (!this.carrierAccount) return;

    const isValid = this.form.validate();
    if (!isValid) return;

    const visibleFormData = Object.entries(this.formData).map((el) => {
      return {
        ...el[1],
        key: el[0],
      };
    });

    if (!visibleFormData.length) return;

    const payload = {};
    visibleFormData.forEach((elem) => {
      if (elem.type === ExtensionInputType.boolean) {
        payload[elem.key] = elem.value ? 'true' : 'false';
      } else {
        payload[elem.key] = elem.value;
      }
    });

    this.isLoading = true;

    if (!this.isEditPage && await this.checkFurtherAuthIntegration(payload)) {
      this.isLoading = false;
      return;
    }

    let response: CarrierAccountResponseType | null = null;
    if (this.isEditPage) {
      response = await editCarrierAccount(this.carrierAccount.id, payload);
    } else {
      response = await addCarrierAccount(this.carrierAccount?.carrierCode, payload);
    }

    if (response && response.successful && response.carrierAccount) {
      this.currentSubmissionOrder += 1;
      if (this.checkNextOrderIsAvailable(this.currentSubmissionOrder)
        && !isFedexRegistration(this.carrierCodeQuery as keyof typeof CarrierCodeType)) {
        showSuccessAlertDialog(this.$t('carrierAccounts.addAccount.alertDialog.successfulDevelopmentKeyAddition') as string);

        this.populateForm(this.currentSubmissionOrder);
        this.isLoading = false;

        if (this.isEditPage) {
          this.carrierAccount.id = response.carrierAccount[0].id;
        }

        eventBus.$emit(eventNames.FETCH_CARRIER_ACCOUNTS);
        return;
      }

      const successMessage = this.isEditPage
        ? this.$t('carrierAccounts.editAccount.alertDialog.successfulEdit')
        : this.$t('carrierAccounts.addAccount.alertDialog.successfulAddition');
      showSuccessAlertDialog(successMessage as string);

      this.redirectToCarrierAccountPage();
      return;
    }

    showErrorAlertDialog(response.errorMessage);
    this.isLoading = false;
  }

  private async checkFurtherAuthIntegration(payload: Record<string, string>) {
    if (!this.carrierCodeQuery) return false;

    let response: { returnUrl?: string | undefined } & GlobalResponseType = {
      successful: false,
      redirectUrl: '',
      errorMessage: '',
    };

    if (this.carrierCodeQuery === 'UPS') {
      response = await registerUPSMerchant();
      this.merchantNotification.message = this.$t('carrierAccounts.merchantRegistration.body', {
        carrier: 'UPS',
      }) as string;
      this.merchantNotification.banner = 'MerchantRedirectUPS';
      this.merchantNotification.beforeAuthorization = async () => {
        if (this.isIncompleteInstallPage && this.carrierAccount?.id) {
          await editCarrierAccount(this.carrierAccount.id, payload);
          return;
        }

        await addCarrierAccount('UPS', payload);
      };
    }

    if (response.successful && response.returnUrl) {
      this.merchantNotification.redirectUrl = response.returnUrl;
      this.merchantNotification.show = true;
      return true;
    }

    return false;
  }

  private getInputFormElementData = (key: string, type: ExtensionInputType): ElementDataType => {
    let elementData: ElementDataType = {
      tag: 'v-text-field',
      type: 'text',
      distinctKey: `${this.carrierCodeQuery}-${key}`,
      autocomplete: 'on',
    };

    switch (key) {
      case 'payorCountryCode':
      case 'devPayorCountryCode':
        elementData.tag = 'CountrySelect';
        elementData.autocomplete = 'off';
        break;
      case 'paymentMethod':
        elementData.tag = 'PaymentMethodSelect';
        break;
      case 'password':
      case 'devPassword':
        elementData = {
          tag: 'PasswordInput',
          type: 'password',
          distinctKey: `${this.carrierCodeQuery}-p`,
          autocomplete: `${this.carrierCodeQuery} new-password`,
        };
        break;
      case 'username':
        elementData.distinctKey = `${this.carrierCodeQuery}-uname`;
        elementData.autocomplete = `${this.carrierCodeQuery} username`;
        break;
      default:
        break;
    }

    if (type === ExtensionInputType.boolean) {
      elementData.tag = 'v-checkbox';
      elementData.type = 'checkbox';
    }

    return elementData;
  }

  private redirectToCarrierAccountPage() {
    eventBus.$emit(eventNames.FETCH_CARRIER_ACCOUNTS);
    this.$router.push({
      name: 'carrier-accounts',
      query: {
        legacy: this.isLegacyFeatures ? 'true' : undefined,
      },
    });
  }

  private checkNextOrderIsAvailable(currentOrder: number): boolean {
    if (!this.carrierAccount) return false;

    const { extensions } = this.carrierAccount;
    if (!extensions) return false;

    return extensions.some((ext) => ext.order === currentOrder);
  }
}
